import { Button, LoadingArea, MarkdownPreview, Spinner } from '@consigli/facade';
import { usePackage, usePackageId, useProjectId } from '@consigli/hooks';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiDownload } from 'react-icons/fi';
import { HiInformationCircle } from 'react-icons/hi';
import { HiSparkles } from 'react-icons/hi2';
import { Navigate } from 'react-router';
import { toast } from 'react-toastify';

import { Breadcrumb } from '@/features/breadcrumb/breadcrumb';
import { useBidReviewContractors } from '@/features/package-creation/bid-review/use-bid-review-contractors';
import { WorkspaceProvider } from '@/layouts/workspace-layout/use-workspace-context';
import { WorkspaceLayout } from '@/layouts/workspace-layout/workspace-layout';
import { Route } from '@/routes';

import { exportBidComparisonToExcel } from './bid-comparison-export';
import { BidComparisonSourcePanel } from './bid-comparison-source-panel';
import { BidComparisonTable } from './bid-comparison-table';
import { useBidComparisonData } from './use-bid-comparison-data';

import type {
  BidComparisonCell,
  BidComparisonCitation,
  BidComparisonRow,
} from './bid-comparison-types';

interface NavigableSource {
  citation: BidComparisonCitation;
  heading: string;
  valueLabel: string;
  value: string;
  rowId: string;
}

const StatCard: FC<{ label: string; value: string | number }> = ({ label, value }) => (
  <div className="flex flex-1 flex-col gap-1 rounded-md border border-gray-200 bg-white px-4 py-3">
    <span className="text-xs font-medium uppercase tracking-wider text-day-neutral-subtle">
      {label}
    </span>
    <span className="text-2xl font-semibold text-day-neutral-dark">{value}</span>
  </div>
);

const BidComparisonContent: FC = () => {
  const { t } = useTranslation();
  const projectId = useProjectId();
  const packageId = usePackageId();
  const { pkg } = usePackage(projectId, packageId);
  const { data, isLoading } = useBidComparisonData();
  const [isDownloading, setIsDownloading] = useState(false);

  // Leveling weights are edited inline in the table but also feed the export, so
  // they live here and are seeded from each row's default whenever the data set
  // changes.
  const [weights, setWeights] = useState<Record<string, number>>({});

  useEffect(() => {
    setWeights(Object.fromEntries(data.rows.map((row) => [row.id, row.weight])));
  }, [data.rows]);

  const handleWeightChange = useCallback((rowId: string, value: string) => {
    const parsed = Number(value);
    setWeights((current) => ({
      ...current,
      [rowId]: Number.isFinite(parsed) ? parsed : 0,
    }));
  }, []);

  const handleDownload = useCallback(async () => {
    if (data.rows.length === 0) {
      toast.info(
        t('bid-comparison.download-empty', 'There is no bid comparison data to export yet.'),
      );
      return;
    }
    setIsDownloading(true);
    try {
      await exportBidComparisonToExcel({ data, weights, packageName: pkg?.name, t });
      toast.success(t('bid-comparison.download-success', 'Bid comparison exported.'));
    } catch (error) {
      console.error('Error exporting bid comparison:', error);
      toast.error(t('bid-comparison.download-error', 'Could not export the bid comparison.'));
    } finally {
      setIsDownloading(false);
    }
  }, [data, weights, pkg?.name, t]);

  const [activeSourceIndex, setActiveSourceIndex] = useState<number>(-1);

  const navigableSources = useMemo((): NavigableSource[] => {
    const sources: NavigableSource[] = [];
    for (const row of data.rows) {
      if (row.reference) {
        sources.push({
          citation: row.reference,
          heading: t('bid-comparison.tender-label', 'Tender'),
          valueLabel: t('bid-comparison.requirement-label', 'Requirement'),
          value: row.item,
          rowId: row.id,
        });
      }
      for (const cell of row.cells) {
        if (cell.citation) {
          const contractorName =
            data.contractors.find((contractor) => contractor.id === cell.contractorId)?.name ??
            cell.contractorId;
          sources.push({
            citation: cell.citation,
            heading: contractorName,
            valueLabel: t('bid-comparison.finding-label', 'Finding'),
            value: cell.value,
            rowId: row.id,
          });
        }
      }
    }
    return sources;
  }, [data.rows, data.contractors, t]);

  const activeSource =
    activeSourceIndex >= 0 ? (navigableSources[activeSourceIndex] ?? null) : null;
  const activeRowId = activeSource?.rowId ?? null;

  const handleViewSource = useCallback(
    (citation: BidComparisonCitation, cell: BidComparisonCell, rowId: string) => {
      const index = navigableSources.findIndex(
        (source) =>
          source.citation.blobId === citation.blobId &&
          source.citation.page === citation.page &&
          source.rowId === rowId &&
          source.value === cell.value,
      );
      setActiveSourceIndex(index >= 0 ? index : -1);
    },
    [navigableSources],
  );

  const handleViewReference = useCallback(
    (row: BidComparisonRow) => {
      if (!row.reference) return;
      const index = navigableSources.findIndex(
        (source) => source.rowId === row.id && source.citation === row.reference,
      );
      setActiveSourceIndex(index >= 0 ? index : -1);
    },
    [navigableSources],
  );

  const handleNext = useCallback(() => {
    setActiveSourceIndex((current) =>
      current < navigableSources.length - 1 ? current + 1 : current,
    );
  }, [navigableSources.length]);

  const handlePrevious = useCallback(() => {
    setActiveSourceIndex((current) => (current > 0 ? current - 1 : current));
  }, []);

  const handleCloseSource = useCallback(() => {
    setActiveSourceIndex(-1);
  }, []);

  return (
    <div className="flex w-full flex-col gap-6 px-8 py-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-bold text-gray-900">
            {t('bid-comparison.title', 'Bid Comparison')}
          </h1>
          <p className="text-sm text-day-neutral-subtle">
            {t(
              'bid-comparison.subtitle',
              'Side-by-side leveling of each contractor’s bid against the tender requirements.',
            )}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            rounded
            primary
            disabled={isDownloading}
            onClick={handleDownload}
            icon={isDownloading ? () => <Spinner size="xsmall" /> : () => <FiDownload size={16} />}
            className="text-sm font-bold"
          >
            {t('bid-comparison.download', 'Download .xlsx')}
          </Button>
        </div>
      </div>

      <div className="flex items-start gap-2 rounded-md border border-gray-300 bg-day-light-3 px-4 py-3 text-sm text-day-neutral-dark">
        <HiInformationCircle className="mt-0.5 shrink-0 text-day-neutral-subtle" size={16} />
        <span>
          {t(
            'bid-comparison.disclaimer',
            'This comparison is AI-assisted and shown for preview. Verify figures against the source documents before making commercial decisions.',
          )}
        </span>
      </div>

      <div className="flex flex-col gap-4 rounded-md border border-gray-200 bg-white p-5 shadow-sm">
        <div className="flex items-center gap-2">
          <HiSparkles className="text-day-neutral-dark" size={18} />
          <h2 className="text-base font-semibold text-day-neutral-dark">
            {t('bid-comparison.ai-summary', 'AI summary')}
          </h2>
        </div>
        {data.summary && (
          <MarkdownPreview content={data.summary} className="text-sm text-gray-700" />
        )}
        <div className="flex flex-col gap-4 sm:flex-row">
          <StatCard
            label={t('bid-comparison.stat-bids', 'Bids compared')}
            value={data.bidsCompared}
          />
          <StatCard
            label={t('bid-comparison.stat-items', 'Items leveled')}
            value={data.itemsLeveled}
          />
          <StatCard
            label={t('bid-comparison.stat-weighting', 'Total weighting')}
            value={`${data.totalWeighting}%`}
          />
        </div>
      </div>

      {isLoading ? (
        <LoadingArea />
      ) : data.rows.length === 0 ? (
        <div className="flex items-center justify-center rounded-md border border-gray-200 bg-white px-4 py-10 text-sm text-day-neutral-subtle shadow-sm">
          {t('bid-comparison.empty', 'No bid comparison data is available yet for this package.')}
        </div>
      ) : (
        <BidComparisonTable
          data={data}
          weights={weights}
          activeRowId={activeRowId}
          onWeightChange={handleWeightChange}
          onViewSource={handleViewSource}
          onViewReference={handleViewReference}
        />
      )}

      {activeSource && (
        <BidComparisonSourcePanel
          citation={activeSource.citation}
          heading={activeSource.heading}
          valueLabel={activeSource.valueLabel}
          value={activeSource.value}
          onClose={handleCloseSource}
          onNext={activeSourceIndex < navigableSources.length - 1 ? handleNext : undefined}
          onPrevious={activeSourceIndex > 0 ? handlePrevious : undefined}
        />
      )}
    </div>
  );
};

export const BidComparisonPage: FC = () => {
  const { t } = useTranslation();

  // Legacy OFFER packages resolve no contractors and have no comparison data, so
  // the comparison view doesn't apply to them. The tab is hidden for such
  // packages; this also sends any direct navigation back to the dashboard rather
  // than rendering a permanently empty comparison page.
  const { contractors, isLoading: contractorsLoading } = useBidReviewContractors();

  if (contractorsLoading) {
    return <LoadingArea />;
  }

  if (contractors.length === 0) {
    return <Navigate replace to={`../${Route.DASHBOARD}`} />;
  }

  return (
    <WorkspaceProvider>
      <WorkspaceLayout>
        <Breadcrumb label={t('bid-comparison.title', 'Bid Comparison')} />
        <BidComparisonContent />
      </WorkspaceLayout>
    </WorkspaceProvider>
  );
};
